import type { TAnyBridgeMethod } from './methods/types';

/**
 * The v1 closure-based bridge method signature: a single untyped payload argument returning a Promise.
 * Adapted to {@link TAnyBridgeMethod} by ignoring the BridgeContext and passing the payload through directly.
 */
type LegacyBridgeMethod = (data: unknown) => Promise<unknown>;

export const adaptLegacyBridgeMethod =
	(method: LegacyBridgeMethod): TAnyBridgeMethod =>
	async (_ctx, payload) => {
		return await method(payload);
	};

/**
 * Merges product-provided custom bridge methods into the base method map.
 *
 * Applies both v1 (`customBridgeMethods`) and v2 (`customBridge`) in order:
 *   base → legacy (v1, adapted to drop context) → customBridge (v2, used as-is)
 *
 * Later entries win, so v2 methods take precedence over v1 methods with the same name,
 * and both override base methods.
 */
export const consolidateCustomBridge = (
	baseBridgeMethods: Record<string, TAnyBridgeMethod>,
	legacyCustomBridgeMethods?: Record<string, (data: unknown) => Promise<unknown>>,
	customBridge?: Record<string, TAnyBridgeMethod>,
): Record<string, TAnyBridgeMethod> => {
	if (!legacyCustomBridgeMethods && !customBridge) {
		return baseBridgeMethods;
	}
	const convertedLegacyMethods = legacyCustomBridgeMethods
		? Object.entries(legacyCustomBridgeMethods).reduce(
				(acc, [methodName, method]) => {
					acc[methodName] = adaptLegacyBridgeMethod(method);
					return acc;
				},
				{} as Record<string, TAnyBridgeMethod>,
			)
		: {};
	return {
		...baseBridgeMethods,
		...convertedLegacyMethods,
		...customBridge,
	};
};
